// apps/web/src/components/MediaPanelEditor.js
// Editor for creating and modifying task media panels

import React, { useState, useEffect } from 'react';
import { validateVideoEmbed } from '../utils/mediaSanitizer';
import './MediaPanelEditor.css';

/**
 * MediaPanelEditor - UI for creating/editing media panels
 *
 * @param {Object} mediaPanel - Current media panel config (or null)
 * @param {Function} onChange - Callback when media panel changes: onChange(newMediaPanel)
 */
export default function MediaPanelEditor({ mediaPanel, onChange }) {
  const [enabled, setEnabled] = useState(mediaPanel?.enabled ?? false);
  const [contentType, setContentType] = useState(mediaPanel?.content_type ?? 'markdown');
  const [content, setContent] = useState(mediaPanel?.content ?? '');
  const [attachments, setAttachments] = useState(mediaPanel?.attachments ?? []);
  const [videoUrl, setVideoUrl] = useState('');
  const [videoError, setVideoError] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Settings with defaults
  const [panelWidth, setPanelWidth] = useState(mediaPanel?.settings?.panel_width ?? '400px');
  const [allowResize, setAllowResize] = useState(mediaPanel?.settings?.allow_resize ?? true);
  const [defaultCollapsed, setDefaultCollapsed] = useState(mediaPanel?.settings?.default_collapsed ?? false);

  // Update parent whenever any field changes
  useEffect(() => {
    if (!enabled) {
      onChange(null);
      return;
    }

    const newMediaPanel = {
      enabled: true,
      content_type: contentType,
      content,
      attachments,
      settings: {
        panel_width: panelWidth,
        panel_height: 'auto',
        allow_resize: allowResize,
        default_collapsed: defaultCollapsed
      }
    };

    onChange(newMediaPanel);
  }, [enabled, contentType, content, attachments, panelWidth, allowResize, defaultCollapsed, onChange]);

  // Add video from URL
  const handleAddVideo = () => {
    setVideoError('');

    if (!videoUrl.trim()) {
      setVideoError('Please enter a video URL');
      return;
    }

    const validated = validateVideoEmbed(videoUrl);

    if (!validated) {
      setVideoError('Invalid video URL. Only YouTube and Vimeo videos are supported.');
      return;
    }

    const newAttachment = {
      id: crypto.randomUUID(),
      type: 'video',
      platform: validated.platform,
      embed_url: validated.embedUrl,
      thumbnail: validated.thumbnail,
      title: '',
      videoId: validated.videoId
    };

    setAttachments([...attachments, newAttachment]);
    setVideoUrl('');
    setVideoError('');
  };

  // Remove attachment
  const handleRemoveAttachment = (id) => {
    setAttachments(attachments.filter(a => a.id !== id));
  };

  // Update attachment title/caption
  const handleUpdateAttachment = (id, field, value) => {
    setAttachments(attachments.map(a =>
      a.id === id ? { ...a, [field]: value } : a
    ));
  };

  return (
    <div className="MediaPanelEditor">
      {/* Enable/Disable Toggle */}
      <div className="MediaPanelEditor-header">
        <label className="MediaPanelEditor-toggle">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
          />
          <span>Enable Media Panel</span>
        </label>
        <p className="MediaPanelEditor-help">
          Add rich content, images, and videos to this task
        </p>
      </div>

      {/* Editor (only shown when enabled) */}
      {enabled && (
        <div className="MediaPanelEditor-body">
          {/* Content Type Selector */}
          <div className="MediaPanelEditor-field">
            <label>Content Type:</label>
            <select
              value={contentType}
              onChange={(e) => setContentType(e.target.value)}
              className="MediaPanelEditor-select"
            >
              <option value="markdown">Markdown</option>
              <option value="plaintext">Plain Text</option>
              <option value="html">HTML (sanitized)</option>
              <option value="richtext">Rich Text</option>
            </select>
            <span className="MediaPanelEditor-hint">
              {contentType === 'markdown' && '✓ Supports **bold**, *italic*, [links](url), etc.'}
              {contentType === 'plaintext' && 'Simple text without formatting'}
              {contentType === 'html' && '⚠️ HTML will be sanitized for security'}
              {contentType === 'richtext' && '✓ Visual rich text editing'}
            </span>
          </div>

          {/* Content Editor */}
          <div className="MediaPanelEditor-field">
            <label>Content:</label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder={
                contentType === 'markdown'
                  ? '# Heading\n\n**Bold text** and *italic*\n\n- List item 1\n- List item 2'
                  : 'Enter your content here...'
              }
              className="MediaPanelEditor-textarea"
              rows={8}
            />
          </div>

          {/* Attachments Section */}
          <div className="MediaPanelEditor-section">
            <h4>Attachments</h4>

            {/* Add Video */}
            <div className="MediaPanelEditor-field">
              <label>Add Video (YouTube/Vimeo):</label>
              <div className="MediaPanelEditor-video-input">
                <input
                  type="text"
                  value={videoUrl}
                  onChange={(e) => setVideoUrl(e.target.value)}
                  placeholder="https://www.youtube.com/watch?v=..."
                  className="MediaPanelEditor-input"
                  onKeyPress={(e) => e.key === 'Enter' && handleAddVideo()}
                />
                <button
                  type="button"
                  onClick={handleAddVideo}
                  className="MediaPanelEditor-btn"
                >
                  Add Video
                </button>
              </div>
              {videoError && (
                <p className="MediaPanelEditor-error">{videoError}</p>
              )}
              <p className="MediaPanelEditor-hint">
                Supported: YouTube, Vimeo (embed URLs or regular URLs)
              </p>
            </div>

            {/* Attachment List */}
            {attachments.length > 0 && (
              <div className="MediaPanelEditor-attachments">
                {attachments.map((attachment) => (
                  <div key={attachment.id} className="MediaPanelEditor-attachment">
                    <div className="MediaPanelEditor-attachment-header">
                      <span className="MediaPanelEditor-attachment-type">
                        {attachment.type === 'video' && `📹 ${attachment.platform}`}
                        {attachment.type === 'image' && '🖼️ Image'}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleRemoveAttachment(attachment.id)}
                        className="MediaPanelEditor-remove-btn"
                      >
                        ✕
                      </button>
                    </div>

                    {attachment.type === 'video' && (
                      <>
                        <input
                          type="text"
                          value={attachment.title || ''}
                          onChange={(e) => handleUpdateAttachment(attachment.id, 'title', e.target.value)}
                          placeholder="Video title (optional)"
                          className="MediaPanelEditor-input"
                        />
                        <div className="MediaPanelEditor-video-preview">
                          <iframe
                            src={attachment.embed_url}
                            title={attachment.title || 'Video preview'}
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                            referrerPolicy="no-referrer-when-downgrade"
                            allowFullScreen
                          />
                        </div>
                      </>
                    )}

                    {attachment.type === 'image' && (
                      <>
                        <input
                          type="text"
                          value={attachment.caption || ''}
                          onChange={(e) => handleUpdateAttachment(attachment.id, 'caption', e.target.value)}
                          placeholder="Image caption (optional)"
                          className="MediaPanelEditor-input"
                        />
                        <img
                          src={attachment.url}
                          alt={attachment.caption || 'Attachment'}
                          className="MediaPanelEditor-image-preview"
                        />
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Advanced Settings */}
          <div className="MediaPanelEditor-section">
            <button
              type="button"
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="MediaPanelEditor-toggle-advanced"
            >
              {showAdvanced ? '▼' : '▶'} Advanced Settings
            </button>

            {showAdvanced && (
              <div className="MediaPanelEditor-advanced">
                <div className="MediaPanelEditor-field">
                  <label>Panel Width:</label>
                  <input
                    type="text"
                    value={panelWidth}
                    onChange={(e) => setPanelWidth(e.target.value)}
                    placeholder="400px"
                    className="MediaPanelEditor-input-small"
                  />
                </div>

                <div className="MediaPanelEditor-field">
                  <label className="MediaPanelEditor-checkbox">
                    <input
                      type="checkbox"
                      checked={allowResize}
                      onChange={(e) => setAllowResize(e.target.checked)}
                    />
                    <span>Allow panel resizing</span>
                  </label>
                </div>

                <div className="MediaPanelEditor-field">
                  <label className="MediaPanelEditor-checkbox">
                    <input
                      type="checkbox"
                      checked={defaultCollapsed}
                      onChange={(e) => setDefaultCollapsed(e.target.checked)}
                    />
                    <span>Start collapsed by default</span>
                  </label>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
